import { FC, useEffect, useState } from "react"
import { toast } from "react-toastify"

import { useTranslation } from "react-i18next"

import { HtmlEditor } from "components/editors/HtmlEditor"
import { Modal } from "components/modals/Modal"

import { CMS_LOCALE_NAMES, SEO_DESCRIPTION_LIMIT, SEO_TITLE_LIMIT } from "services/cms-seo/type"
import { gameService } from "services/game/game.service"
import { CategorySeo, CategorySeoEntry, GameInterface } from "services/game/type"
import { SEO_LOCALES } from "services/seo/type"

import { useBrandSite } from "./BrandSiteContext"
import {
  Counter,
  Field,
  FieldRow,
  ghostButtonClass,
  inputClass,
  LocaleChipBar,
  modalBoxClass,
  primaryButtonClass,
  SectionHeading,
  ToggleRow
} from "./CmsUi"

const FALLBACK_LOCALE = "en"

interface GameSeoModalProps {
  game: GameInterface | null
  onClose: () => void
  onSaved: () => void
}

/**
 * Keyed on the game, not its path, so the metadata travels with the game. The
 * shape a category uses, minus the slug — a game's URL is its identifier.
 */
export const GameSeoModal: FC<GameSeoModalProps> = ({ game, onClose, onSaved }) => {
  const { t } = useTranslation("translation", { useSuspense: false })
  const site = useBrandSite()

  const [seo, setSeo] = useState<CategorySeo>({})
  const [locale, setLocale] = useState(FALLBACK_LOCALE)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!game) return
    setSeo(game.seo ?? {})
    setLocale(FALLBACK_LOCALE)
  }, [game])

  // Rendered unconditionally to keep the exit animation.
  if (!game) {
    return (
      <Modal open={false} handler={onClose}>
        <div />
      </Modal>
    )
  }

  const entry: CategorySeoEntry = (seo[locale as keyof CategorySeo] as CategorySeoEntry) ?? {}

  const setEntry = (patch: Partial<CategorySeoEntry>) =>
    setSeo((prev) => ({ ...prev, [locale]: { ...entry, ...patch } }))

  /** Plain-text fields only; `footer` has its own editor. */
  const text = (key: "title" | "description" | "h1") => entry[key] ?? ""

  const hasSeo = (code: string) => {
    const value = seo[code as keyof CategorySeo] as CategorySeoEntry | undefined
    return Boolean(value?.title || value?.description || value?.h1 || value?.footer?.html?.trim())
  }

  const submit = async () => {
    setSaving(true)
    // A locale with no overridden field resolves to the brand defaults.
    const cleaned: CategorySeo = { ...(seo.noindex ? { noindex: true } : {}) }
    for (const code of SEO_LOCALES) {
      const value = seo[code] as CategorySeoEntry | undefined
      const kept = {
        ...(value?.title ? { title: value.title } : {}),
        ...(value?.description ? { description: value.description } : {}),
        ...(value?.h1 ? { h1: value.h1 } : {}),
        ...(value?.footer?.html?.trim() ? { footer: value.footer } : {})
      }
      if (Object.keys(kept).length > 0) cleaned[code] = kept
    }

    const saved = await gameService.updateGameSeo(game.identifier, cleaned)
    setSaving(false)

    if (saved) {
      toast.success(t("cmsSeo.gameSaved", { defaultValue: "Game SEO saved" }) as string)
      onSaved()
      onClose()
    }
  }

  return (
    <Modal open handler={onClose}>
      <div className={`${modalBoxClass} max-w-2xl`}>
        <h3 className="mb-1 text-lg font-semibold">{game.title}</h3>
        <p className="mb-4 text-xs text-gray-500 dark:text-gray-400">
          {t("cmsSeo.gameSubtitle", { defaultValue: "Game page — search metadata" })}{" "}
          <span className="font-mono break-all">
            {site.host}/en/game/{game.identifier}
          </span>
        </p>

        <p className="mb-4 rounded border border-black/10 p-3 text-xs opacity-70 dark:border-white/10">
          {t("cmsSeo.gameDescriptionNote", {
            defaultValue:
              "The game's Description, edited in Game Management, is separate copy shown in the same block. This sets the page title, the search-result snippet and whether the page may be indexed."
          })}
        </p>

        <ToggleRow
          title={t("cmsSeo.field.index", { defaultValue: "Allow search engines to index" })}
          description={t("cmsSeo.gameNoindexHint", {
            defaultValue: "Off applies to every language, and removes the game from the sitemap"
          })}
          checked={seo.noindex !== true}
          onChange={() => setSeo((prev) => ({ ...prev, noindex: prev.noindex ? undefined : true }))}
        />

        <div className="mb-4 mt-5">
          <LocaleChipBar
            current={locale}
            present={SEO_LOCALES.filter(hasSeo)}
            onSelect={setLocale}
            onCreate={setLocale}
          />
        </div>

        {locale !== FALLBACK_LOCALE && !hasSeo(locale) && (
          <p className="mb-4 text-xs text-gray-500 dark:text-gray-400">
            {t("cmsSeo.gameInherits", {
              defaultValue: "{{name}} has nothing of its own and serves the English copy. Fill a field to override it.",
              name: CMS_LOCALE_NAMES[locale] ?? locale
            })}
          </p>
        )}

        <FieldRow>
          <Field
            label={t("seo.col.title", { defaultValue: "Title" })}
            counter={<Counter length={text("title").length} limit={SEO_TITLE_LIMIT} />}
          >
            <input className={inputClass} value={text("title")} onChange={(e) => setEntry({ title: e.target.value })} />
          </Field>
        </FieldRow>

        <FieldRow>
          <Field
            label={t("seo.metaDescription", { defaultValue: "Meta description" })}
            counter={<Counter length={text("description").length} limit={SEO_DESCRIPTION_LIMIT} />}
          >
            <textarea
              className={inputClass}
              rows={3}
              value={text("description")}
              onChange={(e) => setEntry({ description: e.target.value })}
            />
          </Field>
        </FieldRow>

        <FieldRow>
          <Field label="H1">
            <input className={inputClass} value={text("h1")} onChange={(e) => setEntry({ h1: e.target.value })} />
          </Field>
        </FieldRow>

        <SectionHeading>{t("cmsSeo.footerCopy", { defaultValue: "SEO copy below the page" })}</SectionHeading>

        <p className="mb-3 text-[11.5px] leading-relaxed text-gray-400 dark:text-gray-500">
          {t("cmsSeo.gameFooterCopyHint", {
            defaultValue:
              "Rendered beside the game's own description, under the title. The text is in the page source whichever state it starts in, so search engines read it either way."
          })}
        </p>

        <HtmlEditor
          value={entry.footer?.html ?? ""}
          onChange={(html) => setEntry({ footer: { html, collapsed: entry.footer?.collapsed !== false } })}
          minHeight={140}
        />

        <div className="mt-3">
          <ToggleRow
            title={t("cmsSeo.footerCollapsed", { defaultValue: "Start collapsed" })}
            checked={entry.footer?.collapsed !== false}
            onChange={() =>
              setEntry({
                footer: { html: entry.footer?.html ?? "", collapsed: entry.footer?.collapsed === false }
              })
            }
          />
        </div>

        <div className="modal-action flex-wrap">
          <button type="button" className={ghostButtonClass} onClick={onClose}>
            {t("common.cancel", { defaultValue: "Cancel" })}
          </button>
          <button type="button" className={primaryButtonClass} disabled={saving} onClick={submit}>
            {t("common.save", { defaultValue: "Save" })}
          </button>
        </div>
      </div>
    </Modal>
  )
}
